import { Autocomplete, Grid, Slider, TextField, Typography, useMediaQuery } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react'
import globalStyles from '../../../assets/styles/global.module.scss';
import styles from '../../../assets/styles/componentStyles/voiceAgentStyles/voiceAgent.module.scss'
import templateStyle from '../../../assets/styles/componentStyles/templates.module.scss'
import GlobalButton from '../../utility/globalButton/button';
import { useVoiceAgentStore } from '../../../store/voiceAgentStore';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import { useSearchParams } from 'react-router-dom';
import Required from '../../utility/required';
import { buildDefaultVoiceAgentFieldData, getDefaultLanguageAndVoice, initialCreateAgentPayload, sortVoiceAgentFieldsByDisplayOrder, userSpeaksFirstOptions, VOICE_AGENT_FIELDS_KEY } from './voiceAgentConstants';
import agent from '../../../services/api';
import { GenericDataContext, GenericDataContextPayload } from '../../../context/genericDataContext';
import { hasVoiceAgentManagePermission } from '../../../utils/constants';
import { useGenericStore } from '../../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';
import { voiceAgentFieldDataPayload } from '../../../models/axiosModel';

const CreateVoiceAgent = () => {
    const { setFlagLoading, handleError, apiRoutesPermissionAssignment, userDetail, languageList } = useContext(UserContext) as UserContextPayload;
    const { voiceAgentsModelList } = useContext(GenericDataContext) as GenericDataContextPayload;
    const [searchParams, setSearchParams] = useSearchParams()
    const agentId = searchParams.get('voice_agent_id')

    const { getVoiceAgentFieldAPI, createAgentPayload, setCreateAgentPayload, tempCreateAgentPayload, aiAgentVoices, 
        voiceAgentFieldData
    } = useVoiceAgentStore(useShallow((state) => ({
        getVoiceAgentFieldAPI: state.getVoiceAgentFieldAPI,
        createAgentPayload: state.createAgentPayload,
        setCreateAgentPayload: state.setCreateAgentPayload,
        tempCreateAgentPayload: state.tempCreateAgentPayload,
        aiAgentVoices: state.aiAgentVoices,
        voiceAgentFieldData: state.voiceAgentFieldData
    })))

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const [maxLengthWarningByFieldId, setMaxLengthWarningByFieldId] = useState<Record<string, boolean>>({})

    const breakPoints = {
        smallScreen: useMediaQuery<boolean>('(max-width : 600px) and (min-width : 0px)'),
        flagTabletScreen: useMediaQuery<boolean>('(max-width : 900px) and (min-width : 600px)'),
        flagSmallScreen: useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1000px)'),
        largeScreen: useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1600px)'),
    }

    const handleResetPayload = () => {
        if (agentId) {
            setCreateAgentPayload(tempCreateAgentPayload)
        }
        else {
            const { language_id, ai_agent_voice_id } = getDefaultLanguageAndVoice(languageList, aiAgentVoices);
            setCreateAgentPayload({
                ...initialCreateAgentPayload,
                voice_agent_detail: {
                    ...initialCreateAgentPayload.voice_agent_detail,
                    language_id,
                    ai_agent_voice_id,
                },
                voice_agent_field_data: buildDefaultVoiceAgentFieldData(voiceAgentFieldData),
            })
        }
    }

    const handleVoiceAgentFieldChange = (fieldId: string, value: string | null) => {
        const currentFieldData: voiceAgentFieldDataPayload[] = createAgentPayload.voice_agent_field_data?.length
            ? createAgentPayload.voice_agent_field_data
            : buildDefaultVoiceAgentFieldData(voiceAgentFieldData);
        setCreateAgentPayload({
            ...createAgentPayload,
            voice_agent_field_data: currentFieldData.map((field) =>
                field.voice_agent_field_id === fieldId
                    ? { ...field, voice_agent_field_data_value: value }
                    : field
            ),
        })
    }

    const handleUserSpeaksFirstChange = (value: string) => {
        const currentFieldData: voiceAgentFieldDataPayload[] = createAgentPayload.voice_agent_field_data?.length
            ? createAgentPayload.voice_agent_field_data
            : buildDefaultVoiceAgentFieldData(voiceAgentFieldData);
        setCreateAgentPayload({
            ...createAgentPayload,
            voice_agent_field_data: currentFieldData.map((field) => {
                if (field.voice_agent_field_key === VOICE_AGENT_FIELDS_KEY.USER_SPEAKS_FIRST) {
                    return { ...field, voice_agent_field_data_value: value };
                }
                if (value === 'true' && field.voice_agent_field_key === VOICE_AGENT_FIELDS_KEY.WELCOME_MESSAGE) {
                    return { ...field, voice_agent_field_data_value: null };
                }
                return field;
            }) ?? [],
        })
    }

    const isUserSpeaksFirstEnabled = () => {
        const fields: voiceAgentFieldDataPayload[] = createAgentPayload.voice_agent_field_data?.length
            ? createAgentPayload.voice_agent_field_data
            : buildDefaultVoiceAgentFieldData(voiceAgentFieldData);
        const userSpeaksFirstField = fields.find((field: voiceAgentFieldDataPayload) => field.voice_agent_field_key === VOICE_AGENT_FIELDS_KEY.USER_SPEAKS_FIRST);
        return (userSpeaksFirstField?.voice_agent_field_data_value ?? 'false') === 'true';
    }

    const getVoiceAgentFieldsToRender = (): voiceAgentFieldDataPayload[] => {
        const fields: voiceAgentFieldDataPayload[] = createAgentPayload.voice_agent_field_data?.length
            ? createAgentPayload.voice_agent_field_data
            : buildDefaultVoiceAgentFieldData(voiceAgentFieldData);
        const sortedFields = sortVoiceAgentFieldsByDisplayOrder(fields);
        if (isUserSpeaksFirstEnabled()) {
            return sortedFields.filter((field: voiceAgentFieldDataPayload) => field.voice_agent_field_key !== VOICE_AGENT_FIELDS_KEY.WELCOME_MESSAGE);
        }
        return sortedFields;
    }

    const getVoiceAgentFieldTitle = (field: voiceAgentFieldDataPayload) => {
        const voiceAgentFieldKey = field.voice_agent_field_key;
        switch(voiceAgentFieldKey){
            case VOICE_AGENT_FIELDS_KEY.USER_SPEAKS_FIRST:
                return 'Welcome Message';
            case VOICE_AGENT_FIELDS_KEY.WELCOME_MESSAGE:
                return null;
            default:
                return field?.voice_agent_field_name;
        }
    }

    const shouldShowVoiceAgentFieldDescription = (field: voiceAgentFieldDataPayload) => {
        if (
            field.voice_agent_field_key === VOICE_AGENT_FIELDS_KEY.USER_SPEAKS_FIRST ||
            field.voice_agent_field_key === VOICE_AGENT_FIELDS_KEY.WELCOME_MESSAGE
        ) {
            return false;
        }
        return Boolean(field.voice_agent_field_description);
    }

    const isVoiceAgentSliderField = (field: voiceAgentFieldDataPayload) => {
        return field.voice_agent_field_type === 'numeric' || field.voice_agent_field_type === 'integer';
    }

    const formatVoiceAgentSliderValue = (field: voiceAgentFieldDataPayload, value: number) => {
        if (field.voice_agent_field_key === VOICE_AGENT_FIELDS_KEY.MAX_CALL_DURATION_SEC) {
            return `${Math.round(value / 60)} Min`;
        }
        return value;
    }

    const renderVoiceAgentFieldControl = (field: voiceAgentFieldDataPayload) => {
        const isDisabled = !hasVoiceAgentManagePermission(apiRoutesPermissionAssignment, userDetail);
        const currentValue = field.voice_agent_field_data_value;

        if (field.voice_agent_field_key === VOICE_AGENT_FIELDS_KEY.USER_SPEAKS_FIRST) {
            const selectedOption = userSpeaksFirstOptions.find(
                (option) => option.value === (currentValue ?? 'false')
            ) ?? userSpeaksFirstOptions[1];

            return (
                <Autocomplete
                    className={`${globalStyles.autocompleteParaphrasing} ${styles.autoComplete}`}
                    options={userSpeaksFirstOptions}
                    value={selectedOption}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    onChange={(_, value) => {
                        handleUserSpeaksFirstChange(value?.value ?? 'false');
                    }}
                    getOptionLabel={(option) => option.label}
                    disabled={isDisabled}
                    disableClearable
                    renderInput={(params) => <TextField {...params} placeholder='Select who speaks first' />}
                    sx={{ padding: "2px 4px !important" }}
                />
            );
        }

        if (field.voice_agent_field_type === 'text') {
            const maxLength = field.voice_agent_field_max_value
                ? Number(field.voice_agent_field_max_value)
                : undefined;
            const showMaxLengthWarning = Boolean(
                maxLength && maxLengthWarningByFieldId[field.voice_agent_field_id]
            );

            return (
                <Grid container direction="column" gap={0.5}>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            minRows={3}
                            multiline
                            className={`${templateStyle.templateGlobalTextfield} ${styles.agentTextAreaStyle}`}
                            value={currentValue ?? ''}
                            onChange={(event) => {
                                const nextValue = event.target.value;
                                if (maxLength && nextValue.length > maxLength) {
                                    setMaxLengthWarningByFieldId((prev) => ({
                                        ...prev,
                                        [field.voice_agent_field_id]: true,
                                    }));
                                    return;
                                }
                                if (maxLength) {
                                    setMaxLengthWarningByFieldId((prev) => ({
                                        ...prev,
                                        [field.voice_agent_field_id]: nextValue.length >= maxLength,
                                    }));
                                }
                                handleVoiceAgentFieldChange(
                                    field.voice_agent_field_id,
                                    nextValue || null
                                );
                            }}
                            onBlur={() => {
                                setMaxLengthWarningByFieldId((prev) => ({
                                    ...prev,
                                    [field.voice_agent_field_id]: false,
                                }));
                            }}
                            fullWidth
                            autoComplete='off'
                            placeholder={`Enter ${field.voice_agent_field_name}`}
                            disabled={isDisabled}
                        />
                    </Grid>
                    {showMaxLengthWarning &&
                        <Grid item xs={12}>
                            <Typography className={styles.voiceAgentFieldMaxLengthWarning}>
                                {`Maximum character limit of ${maxLength} reached`}
                            </Typography>
                        </Grid>
                    }
                </Grid>
            );
        }

        const min = Number(field.voice_agent_field_min_value ?? 0);
        const max = Number(field.voice_agent_field_max_value ?? 100);
        const isInterruptionSensitivity =
            field.voice_agent_field_key === VOICE_AGENT_FIELDS_KEY.INTERRUPTION_SENSITIVITY;
        const isMaxCallDuration =
            field.voice_agent_field_key === VOICE_AGENT_FIELDS_KEY.MAX_CALL_DURATION_SEC;
        const step = isInterruptionSensitivity
            ? 0.1
            : isMaxCallDuration
                ? 60
                : field.voice_agent_field_type === 'numeric'
                    ? 0.01
                    : 1;
        const sliderMin = isMaxCallDuration ? Math.max(60, Math.ceil(min / 60) * 60 || 60) : min;
        const rawSliderValue = Number(currentValue ?? field.voice_agent_field_default_value ?? sliderMin);
        const sliderValue = isMaxCallDuration
            ? Math.min(max, Math.max(sliderMin, Math.round(rawSliderValue / 60) * 60))
            : rawSliderValue;

        return (
            <Slider
                value={sliderValue}
                min={sliderMin}
                max={max}
                step={step}
                valueLabelDisplay="on"
                valueLabelFormat={(value) => formatVoiceAgentSliderValue(field, value)}
                disabled={isDisabled}
                onChange={(_, value) => handleVoiceAgentFieldChange(
                    field.voice_agent_field_id,
                    String(value)
                )}
                className={`${styles.voiceAgentFieldSlider} ${globalStyles.maxWidthFull}`}
            />
        );
    }

    const isSaveDisabled = () => {
        const name = createAgentPayload?.voice_agent_name?.trim() ?? "";
        const desc = createAgentPayload?.voice_agent_description?.trim() ?? "";
        const prompt = createAgentPayload?.voice_agent_detail?.voice_agent_detail_prompt?.trim() ?? "";
        const agentVoice = createAgentPayload?.voice_agent_detail?.ai_agent_voice_id?.trim() ?? "";
        const language = createAgentPayload?.voice_agent_detail?.language_id;
        const model = createAgentPayload?.voice_agent_detail?.voice_agent_model_id;
        
        return !name || !desc || !prompt || !agentVoice || language === null || !model;
    }

    const handleSaveAgentDetails = async () => {
        setFlagLoading((prev) => ({...prev, flagRoundLoader: true}));
        if (agentId) {
            const res = await agent.updateVoiceAgent.put(createAgentPayload)
            if (res?.status) {
                showSuccess("Voice Agent Updated Successfully!")
            }
            else {
                handleError(res)
            }
        }
        else {
            const res = await agent.createVoiceAgent.post(createAgentPayload)
            if (res?.status) {
                const createdVoiceAgentId = res?.data?.voice_agent?.voice_agent_id
                if (createdVoiceAgentId) {
                    setSearchParams((prev) => {
                        const nextParams = new URLSearchParams(prev)
                        nextParams.set('voice_agent_id', createdVoiceAgentId)
                        return nextParams
                    })
                }
                showSuccess("Voice Agent Created Successfully!")
            }
            else {
                handleError(res)
            }
        }
        setFlagLoading((prev) => ({...prev, flagRoundLoader: false}));
    }

    useEffect(() => {
        (async () => {
            if(!voiceAgentFieldData){
                await getVoiceAgentFieldAPI();
            }
        })();
    }, []);

    useEffect(() => {
        if (agentId || !voiceAgentFieldData?.length || createAgentPayload.voice_agent_field_data?.length) {
            return;
        }
        setCreateAgentPayload({
            ...createAgentPayload,
            voice_agent_field_data: buildDefaultVoiceAgentFieldData(voiceAgentFieldData),
        });
    }, [voiceAgentFieldData, agentId]);

    useEffect(() => {
        if (agentId || !languageList?.length || !aiAgentVoices?.length) {
            return;
        }

        const { language_id, ai_agent_voice_id } = getDefaultLanguageAndVoice(languageList, aiAgentVoices);
        const needsLanguage = createAgentPayload.voice_agent_detail?.language_id == null && language_id != null;
        const needsVoice = !createAgentPayload.voice_agent_detail?.ai_agent_voice_id && Boolean(ai_agent_voice_id);

        if (!needsLanguage && !needsVoice) {
            return;
        }

        setCreateAgentPayload({
            ...createAgentPayload,
            voice_agent_detail: {
                ...createAgentPayload.voice_agent_detail,
                ...(needsLanguage ? { language_id } : {}),
                ...(needsVoice ? { ai_agent_voice_id } : {}),
            },
        });
    }, [languageList, aiAgentVoices, agentId]);

    const innerHeight = (window.innerHeight - 115);

    return (
        <Grid container gap={1} className={styles.createVoiceAgentContainer} sx={{height: `${innerHeight}px !important`, maxHeight: `${innerHeight}px`}}>
            <Grid item xs={12} className={styles.voiceFormStyle}>
                <Grid container flexWrap={{lg: "nowrap"}} gap={3} className={styles.voiceAgentFormColumns}>
                    <Grid item xs={12} lg={6} className={styles.voiceAgentFormColumn} sx={{ flex: { lg: '1 1 0' } }}>
                        <Grid container gap={3}>
                            <Grid item xs={12}>
                                <Grid container gap={1}>
                                    <Grid item xs={12}>
                                        <Typography component={'span'} className={styles.formHeadingStyle}>Agent Name </Typography><Required />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            id="outlined-basic"
                                            variant="outlined"
                                            fullWidth
                                            className={`${templateStyle.templateGlobalTextfield} ${styles.agentTextFieldStyle}`}
                                            placeholder='Enter Agent Name'
                                            size='medium'
                                            value={createAgentPayload.voice_agent_name}
                                            onChange={(e) => {
                                                setCreateAgentPayload({
                                                    ...createAgentPayload,
                                                    voice_agent_name: e.target.value
                                                })
                                            }}
                                            disabled={!hasVoiceAgentManagePermission(apiRoutesPermissionAssignment, userDetail)}
                                            autoComplete='off'
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container gap={1}>
                                    <Grid item xs={12}>
                                        <Typography component={'span'} className={styles.formHeadingStyle}>Agent Description </Typography><Required />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            variant="outlined"
                                            minRows={2}
                                            multiline
                                            className={`${templateStyle.templateGlobalTextfield} ${styles.agentTextAreaStyle}`}
                                            value={createAgentPayload.voice_agent_description}
                                            onChange={(event) => {
                                                setCreateAgentPayload({
                                                    ...createAgentPayload,
                                                    voice_agent_description: event.target.value
                                                })
                                            }}
                                            fullWidth
                                            autoComplete='off'
                                            placeholder='Enter Agent Description'
                                            disabled={!hasVoiceAgentManagePermission(apiRoutesPermissionAssignment, userDetail)}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container gap={1}>
                                    <Grid item xs={12}>
                                        <Typography component={'span'} className={styles.formHeadingStyle}>Model</Typography><Required />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            className={`${globalStyles.autocompleteParaphrasing} ${styles.autoComplete}`}
                                            value={voiceAgentsModelList?.find((models) => models?.voice_agent_model_id == createAgentPayload?.voice_agent_detail?.voice_agent_model_id) || null}
                                            isOptionEqualToValue={(option, value) =>
                                                option?.voice_agent_model_id === value?.voice_agent_model_id
                                            }
                                            onChange={(event: React.SyntheticEvent, value) => {
                                                setCreateAgentPayload({
                                                    ...createAgentPayload,
                                                    voice_agent_detail: {
                                                        ...createAgentPayload.voice_agent_detail,
                                                        voice_agent_model_id: value?.voice_agent_model_id || ""
                                                    }
                                                })
                                            }}
                                            getOptionLabel={(option) => `${option?.voice_agent_model_name}`}
                                            disabled={!hasVoiceAgentManagePermission(apiRoutesPermissionAssignment, userDetail)}
                                            options={voiceAgentsModelList || []}
                                            renderInput={(params) => <TextField {...params} placeholder='Select Model' />}
                                            sx={{ padding: "2px 4px !important" }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container gap={1}>
                                    <Grid item xs={12}>
                                        <Typography component={'span'} className={styles.formHeadingStyle}>Agent Language </Typography><Required />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            className={`${globalStyles.autocompleteParaphrasing} ${styles.autoComplete}`}
                                            value={languageList?.find((language) => language?.language_id == createAgentPayload?.voice_agent_detail?.language_id) || null}
                                            isOptionEqualToValue={(option, value) =>
                                                option?.language_id === value?.language_id
                                            }
                                            onChange={(event: React.SyntheticEvent, value) => {
                                                setCreateAgentPayload({
                                                    ...createAgentPayload,
                                                    voice_agent_detail: {
                                                        ...createAgentPayload.voice_agent_detail,
                                                        language_id: value?.language_id || "",
                                                    }
                                                })
                                            }}
                                            getOptionLabel={(option) => `${option?.name}`}
                                            disabled={!hasVoiceAgentManagePermission(apiRoutesPermissionAssignment, userDetail)}
                                            options={languageList || []}
                                            renderInput={(params) => <TextField {...params} placeholder='Select Agent Language' />}
                                            sx={{ padding: "2px 4px !important" }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container gap={1}>
                                    <Grid item xs={12}>
                                        <Typography component={'span'} className={styles.formHeadingStyle}>Agent Voice </Typography><Required />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            className={`${globalStyles.autocompleteParaphrasing} ${styles.autoComplete}`}
                                            value={aiAgentVoices ? aiAgentVoices?.find((agents) => agents.ai_agent_voice_id == createAgentPayload?.voice_agent_detail?.ai_agent_voice_id) : null}
                                            onChange={(event: React.SyntheticEvent, value) => {
                                                setCreateAgentPayload({
                                                    ...createAgentPayload,
                                                    voice_agent_detail: {
                                                        ...createAgentPayload.voice_agent_detail,
                                                        ai_agent_voice_id: value?.ai_agent_voice_id || ""
                                                    }
                                                })
                                            }}
                                            getOptionLabel={(option) => `${option?.ai_agent_voice_name} (${option?.ai_agent_voice_gender})`}
                                            disabled={!hasVoiceAgentManagePermission(apiRoutesPermissionAssignment, userDetail)}
                                            options={aiAgentVoices || []}
                                            renderInput={(params) => <TextField {...params} placeholder='Select Agent Voice' />}
                                            sx={{ padding: "2px 4px !important" }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container gap={1}>
                                    <Grid item xs={12}>
                                        <Typography component={'span'} className={styles.formHeadingStyle}>Agent Prompt </Typography><Required />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            variant="outlined"
                                            minRows={4}
                                            multiline
                                            className={`${templateStyle.templateGlobalTextfield} ${styles.agentTextAreaStyle}`}
                                            value={createAgentPayload?.voice_agent_detail?.voice_agent_detail_prompt}
                                            onChange={(event) => { 
                                                setCreateAgentPayload({
                                                    ...createAgentPayload,
                                                    voice_agent_detail: {
                                                        ...createAgentPayload.voice_agent_detail,
                                                        voice_agent_detail_prompt: event.target.value
                                                    }
                                                })
                                            }}
                                            fullWidth
                                            autoComplete='off'
                                            placeholder='Enter Agent Prompt'
                                            disabled={!hasVoiceAgentManagePermission(apiRoutesPermissionAssignment, userDetail)}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} lg={6} className={styles.voiceAgentFormColumn} sx={{ flex: { lg: '1 1 0' } }}>
                        <Grid container gap={3}>
                            {getVoiceAgentFieldsToRender().map((field) => (
                                <Grid item xs={12} key={field.voice_agent_field_id}>
                                    <Grid
                                        container
                                        gap={isVoiceAgentSliderField(field) ? 0 : 2}
                                        direction={"column"}
                                    >
                                        {getVoiceAgentFieldTitle(field) &&
                                            <Grid item xs={12}>
                                                <Grid container direction={"column"}>
                                                    <Grid item xs={12}>
                                                        <Typography component={'span'} className={styles.formHeadingStyle}>
                                                            {getVoiceAgentFieldTitle(field)}
                                                        </Typography>
                                                    </Grid>
                                                    {shouldShowVoiceAgentFieldDescription(field) &&
                                                        <Grid item xs={12}>
                                                            <Typography className={styles.voiceAgentFieldDescStyle}>
                                                                {field.voice_agent_field_description}
                                                            </Typography>
                                                        </Grid>
                                                    }
                                                </Grid>
                                            </Grid>
                                        }
                                        <Grid
                                            item
                                            xs={12}
                                            className={isVoiceAgentSliderField(field) ? styles.voiceAgentFieldSliderWrap : undefined}
                                        >
                                            {renderVoiceAgentFieldControl(field)}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            {hasVoiceAgentManagePermission(apiRoutesPermissionAssignment, userDetail) && 
                <Grid item xs={12} className={styles.voiceFormButtonStyle}>
                    <Grid container direction={"row"} gap={2} alignItems={"center"}>
                        <Grid item>
                            <GlobalButton
                                buttonText={"Cancel"}
                                className='borderedButtonStyle'
                                sx={{ minWidth: breakPoints.smallScreen ? "100px" : "130px", fontSize: breakPoints.largeScreen ? "13px !important" : "" }}
                                onClick={() => handleResetPayload()}
                            />
                        </Grid>
                        <Grid item>
                            <GlobalButton
                                buttonText={agentId ? "Update" : "Create"}
                                className='primaryButtonStyle'
                                sx={{ minWidth: breakPoints.smallScreen ? "100px" : "130px" }}
                                disabled={isSaveDisabled()}
                                onClick={() => handleSaveAgentDetails()}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            }
        </Grid>
        
    )
}

export default CreateVoiceAgent
